import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CalendarDays, Plus, Clock, MapPin, MessageCircle, Trash2, Lock, ChevronDown, ChevronUp, Loader2, StickyNote } from "lucide-react";
import { waLink } from "./ConnectPersonModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const APPT_STATUS = [
  { value: "agendado", label: "Agendado", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "realizado", label: "Realizado", cls: "bg-green-50 text-green-700 border-green-200" },
  { value: "cancelado", label: "Cancelado", cls: "bg-gray-100 text-gray-600 border-gray-200" },
  { value: "faltou", label: "Faltou", cls: "bg-red-50 text-red-700 border-red-200" },
];

export const fmtDateBR = (d) => (d ? d.split("T")[0].split("-").reverse().join("/") : "");
const todayStr = () => new Date().toISOString().split("T")[0];

export const appointmentMessage = (appt, nome) =>
  `Olá ${(nome || appt.pessoa_nome || "").split(" ")[0]}, sua sessão está marcada para ${fmtDateBR(appt.data)} às ${appt.hora}${appt.local ? ` em ${appt.local}` : ""}. Até lá!`;

export const nextAppointmentByPessoa = (appointments) => {
  const today = todayStr();
  const map = {};
  [...appointments]
    .filter(a => a.status === "agendado" && a.data >= today)
    .sort((a, b) => `${a.data}${a.hora}`.localeCompare(`${b.data}${b.hora}`))
    .forEach(a => { if (!map[a.pessoa_id]) map[a.pessoa_id] = a; });
  return map;
};

const AppointmentCard = ({ appt, canManage, onStatus, onDelete, onAddNote, onDeleteNote }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const st = APPT_STATUS.find(s => s.value === appt.status) || APPT_STATUS[0];
  const link = waLink(appt.telefone, appointmentMessage(appt));

  const submitNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    await onAddNote(appt, note.trim());
    setNote("");
    setSaving(false);
  };

  return (
    <div className="rounded-xl border bg-card p-3 space-y-2" data-testid={`appointment-${appt.id}`}>
      <div className="flex items-start gap-3">
        <div className="text-center shrink-0 w-14">
          <p className="text-lg font-bold leading-none">{appt.hora}</p>
          <p className="text-[10px] text-muted-foreground">{appt.duracao_min} min</p>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{appt.pessoa_nome}</p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
            {appt.local && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{appt.local}</span>}
            {appt.observacoes && <span className="truncate">{appt.observacoes}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {link && canManage && (
            <Button asChild variant="ghost" size="icon" className="h-9 w-9 text-green-600" title="Lembrar pelo WhatsApp">
              <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`appointment-whatsapp-${appt.id}`}><MessageCircle className="h-4 w-4" /></a>
            </Button>
          )}
          {canManage ? (
            <Select value={appt.status} onValueChange={(v) => onStatus(appt, v)}>
              <SelectTrigger className={`h-8 w-[112px] text-xs border ${st.cls}`} data-testid={`appointment-status-${appt.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{APPT_STATUS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Badge variant="outline" className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
          )}
        </div>
      </div>
      {canManage && (
        <div className="flex items-center justify-between pt-1 border-t">
          <button onClick={() => setOpen(v => !v)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid={`appointment-notes-toggle-${appt.id}`}>
            <Lock className="h-3 w-3" /> Anotações privadas ({appt.anotacoes_privadas?.length || 0})
            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => onDelete(appt)} data-testid={`appointment-delete-${appt.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
      {canManage && open && (
        <div className="space-y-2 rounded-lg bg-amber-50/60 border border-amber-100 p-2" data-testid={`appointment-notes-${appt.id}`}>
          <p className="text-[10px] text-amber-800 flex items-center gap-1"><Lock className="h-3 w-3" /> Visível apenas para líderes</p>
          {(appt.anotacoes_privadas || []).map(n => (
            <div key={n.id} className="flex gap-2 text-xs bg-white rounded-md p-2 border" data-testid={`note-${n.id}`}>
              <StickyNote className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="whitespace-pre-wrap">{n.texto}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{n.autor} · {n.created_at ? new Date(n.created_at).toLocaleString("pt-BR") : ""}</p>
              </div>
              <button onClick={() => onDeleteNote(appt, n)} className="text-muted-foreground hover:text-destructive" data-testid={`note-delete-${n.id}`}><Trash2 className="h-3 w-3" /></button>
            </div>
          ))}
          <div className="flex gap-2">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Registrar evolução, observações da sessão..." className="text-xs bg-white" data-testid={`note-input-${appt.id}`} />
            <Button size="sm" onClick={submitNote} disabled={saving || !note.trim()} className="self-end" data-testid={`note-save-${appt.id}`}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Salvar"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ProjectAgendaTab({ project, participants = [], canManage, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);
  const [pessoaFilter, setPessoaFilter] = useState("todos");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ pessoa_id: "", data: todayStr(), hora: "09:00", duracao_min: 50, local: project?.local || "", observacoes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${project.id}/appointments`);
      setItems(res.data || []);
      onChanged?.(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar agenda");
    } finally {
      setLoading(false);
    }
  }, [project?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setForm(f => ({ ...f, local: f.local || project?.local || "" })); }, [project?.local]);

  const save = async () => {
    if (!form.pessoa_id || !form.data || !form.hora) { toast.error("Informe paciente, data e hora"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${project.id}/appointments`, { ...form, duracao_min: Number(form.duracao_min) || 50 });
      toast.success("Sessão agendada");
      setDialogOpen(false);
      setForm(f => ({ ...f, pessoa_id: "", observacoes: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao agendar");
    } finally {
      setSaving(false);
    }
  };

  const onStatus = async (appt, status) => {
    try {
      await axios.put(`${API}/projects/${project.id}/appointments/${appt.id}`, { status });
      setItems(prev => prev.map(a => (a.id === appt.id ? { ...a, status } : a)));
    } catch (e) { toast.error("Erro ao atualizar"); }
  };
  const onDelete = async (appt) => {
    if (!window.confirm(`Remover sessão de ${appt.pessoa_nome} em ${fmtDateBR(appt.data)}?`)) return;
    try {
      await axios.delete(`${API}/projects/${project.id}/appointments/${appt.id}`);
      toast.success("Sessão removida");
      load();
    } catch (e) { toast.error("Erro ao remover"); }
  };
  const onAddNote = async (appt, texto) => {
    try {
      const res = await axios.post(`${API}/projects/${project.id}/appointments/${appt.id}/notes`, { texto });
      setItems(prev => prev.map(a => (a.id === appt.id ? { ...a, anotacoes_privadas: [...(a.anotacoes_privadas || []), res.data] } : a)));
    } catch (e) { toast.error("Erro ao salvar anotação"); }
  };
  const onDeleteNote = async (appt, note) => {
    try {
      await axios.delete(`${API}/projects/${project.id}/appointments/${appt.id}/notes/${note.id}`);
      setItems(prev => prev.map(a => (a.id === appt.id ? { ...a, anotacoes_privadas: (a.anotacoes_privadas || []).filter(n => n.id !== note.id) } : a)));
    } catch (e) { toast.error("Erro ao remover anotação"); }
  };

  const today = todayStr();
  const visible = items
    .filter(a => showPast || a.data >= today)
    .filter(a => pessoaFilter === "todos" || a.pessoa_id === pessoaFilter);
  const grouped = visible.reduce((acc, a) => { (acc[a.data] = acc[a.data] || []).push(a); return acc; }, {});
  const dates = Object.keys(grouped).sort((a, b) => (showPast ? b.localeCompare(a) : a.localeCompare(b)));

  return (
    <div className="space-y-3" data-testid="project-agenda-tab">
      <div className="flex flex-wrap items-center gap-2">
        {canManage && (
          <Button onClick={() => setDialogOpen(true)} className="gap-2 h-10" data-testid="agenda-new-btn">
            <Plus className="h-4 w-4" /> Nova sessão
          </Button>
        )}
        <div className="flex gap-1 p-1 bg-muted/50 rounded-full">
          <button onClick={() => setShowPast(false)} className={`px-3 py-1 rounded-full text-xs ${!showPast ? "bg-primary text-primary-foreground" : ""}`} data-testid="agenda-upcoming-btn">Próximas</button>
          <button onClick={() => setShowPast(true)} className={`px-3 py-1 rounded-full text-xs ${showPast ? "bg-primary text-primary-foreground" : ""}`} data-testid="agenda-all-btn">Histórico</button>
        </div>
        {canManage && participants.length > 0 && (
          <Select value={pessoaFilter} onValueChange={setPessoaFilter}>
            <SelectTrigger className="h-8 w-[160px] text-xs ml-auto" data-testid="agenda-person-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os pacientes</SelectItem>
              {participants.map(p => <SelectItem key={p.pessoa_id} value={p.pessoa_id}>{p.pessoa_nome || p.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : dates.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground" data-testid="agenda-empty">
          <CalendarDays className="h-12 w-12 mx-auto mb-2 opacity-40" />
          <p>{showPast ? "Nenhuma sessão registrada" : "Nenhuma sessão agendada"}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {dates.map(d => (
            <div key={d} className="space-y-2">
              <p className={`text-xs font-semibold flex items-center gap-1.5 ${d === today ? "text-primary" : "text-muted-foreground"}`}>
                <Clock className="h-3.5 w-3.5" /> {d === today ? "Hoje · " : ""}{fmtDateBR(d)}
              </p>
              {grouped[d].map(a => (
                <AppointmentCard key={a.id} appt={a} canManage={canManage} onStatus={onStatus} onDelete={onDelete} onAddNote={onAddNote} onDeleteNote={onDeleteNote} />
              ))}
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="agenda-new-dialog">
          <DialogHeader><DialogTitle>Nova sessão</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Paciente *</Label>
              <Select value={form.pessoa_id} onValueChange={(v) => setForm({ ...form, pessoa_id: v })}>
                <SelectTrigger data-testid="agenda-form-person"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {participants.map(p => <SelectItem key={p.pessoa_id} value={p.pessoa_id}>{p.pessoa_nome || p.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Data *</Label>
              <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} className="w-full min-w-0 h-11 appearance-none" data-testid="agenda-form-date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="min-w-0">
                <Label className="text-xs">Hora *</Label>
                <Input type="time" value={form.hora} onChange={(e) => setForm({ ...form, hora: e.target.value })} className="w-full min-w-0 h-11 appearance-none" data-testid="agenda-form-time" />
              </div>
              <div className="min-w-0">
                <Label className="text-xs">Duração (min)</Label>
                <Input type="number" min={5} step={5} value={form.duracao_min} onChange={(e) => setForm({ ...form, duracao_min: e.target.value })} className="w-full min-w-0 h-11" data-testid="agenda-form-duration" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Local {!project?.local && <span className="text-muted-foreground">(será salvo também na inscrição)</span>}</Label>
              <Input value={form.local} onChange={(e) => setForm({ ...form, local: e.target.value })} placeholder={project?.local || "Endereço da sessão"} data-testid="agenda-form-local" />
            </div>
            <div>
              <Label className="text-xs">Observação para o paciente</Label>
              <Input value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} placeholder="Ex: trazer exames" data-testid="agenda-form-obs" />
            </div>
            <Button onClick={save} disabled={saving} className="w-full" data-testid="agenda-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalendarDays className="h-4 w-4 mr-2" />} Agendar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
